import React from 'react';
import { Select } from '../../../components/select/select';
import { SelectContent } from '../../../components/select/select-content';
import { SelectItem } from '../../../components/select/select-item';
import { SelectTrigger } from '../../../components/select/select-trigger';
import { SelectValue } from '../../../components/select/select-value';
import { cn } from '../../../utils/cn';

export const DEFAULT_PAGE_SIZE_OPTIONS = [12, 24, 48, 72] as const;

interface PaginationRange {
    from: number;
    to: number;
    total: number;
}

interface PaginationPageSizeControlProps {
    className?: string;
    currentPage: number;
    pageSize: number;
    totalCount: number;
    itemLabel?: string;
    pageSizeOptions?: number[];
    onPageSizeChange: (nextPageSize: number) => void;
}

/**
 * Computes visible range values for paginated content.
 */
export const getPaginationRange = ({
    currentPage,
    pageSize,
    totalCount,
}: Pick<
    PaginationPageSizeControlProps,
    'currentPage' | 'pageSize' | 'totalCount'
>): PaginationRange => {
    const safeTotal = Math.max(0, totalCount);

    if (safeTotal === 0) {
        return {
            from: 0,
            to: 0,
            total: 0,
        };
    }

    const safePage = Math.max(1, currentPage);
    const safePageSize = Math.max(1, pageSize);
    const start = (safePage - 1) * safePageSize + 1;

    return {
        from: Math.min(start, safeTotal),
        to: Math.min(start + safePageSize - 1, safeTotal),
        total: safeTotal,
    };
};

/**
 * Standalone companion component for page-size selection and range summary.
 * Use it with or without page-link pagination controls.
 */
export const PaginationPageSizeControl = ({
    className,
    currentPage,
    pageSize,
    totalCount,
    itemLabel = 'items',
    pageSizeOptions = [...DEFAULT_PAGE_SIZE_OPTIONS],
    onPageSizeChange,
}: PaginationPageSizeControlProps) => {
    const range = getPaginationRange({ currentPage, pageSize, totalCount });
    const itemLabelForControl =
        itemLabel.length > 0
            ? `${itemLabel.charAt(0).toUpperCase()}${itemLabel.slice(1)}`
            : itemLabel;
    const uniqueOptions = Array.from(
        new Set([...pageSizeOptions, Math.max(1, pageSize)]),
    ).sort((left, right) => left - right);

    return (
        <div
            className={cn(
                'flex flex-wrap items-center justify-between gap-3 text-sm text-gray-700',
                className,
            )}
        >
            <p className="m-0">
                {range.from} - {range.to} of {range.total} {itemLabel}
            </p>

            <span aria-hidden="true">|</span>

            <div className="flex items-center gap-2">
                <span>{itemLabelForControl} per page:</span>
                <Select
                    value={String(pageSize)}
                    onValueChange={(value) => {
                        const nextPageSize = Number(value);

                        if (!Number.isNaN(nextPageSize)) {
                            onPageSizeChange(nextPageSize);
                        }
                    }}
                >
                    <SelectTrigger
                        color="lightOutline"
                        variant="small"
                        className="h-8 min-w-20 rounded-md"
                        aria-label={`${itemLabel} per page`}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {uniqueOptions.map((option) => (
                            <SelectItem key={option} value={String(option)}>
                                {option}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};
